import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useSyncActivity } from '@/hooks/useSyncActivity';
import { useCurrentPerson } from '@/hooks/useCurrentPerson';
import { hasPendingDocuments } from '@/db/services/document_service';
import { hasPendingCaseNotes } from '@/db/services/case_note_service';
import { describeLeftBehind } from '@/lib/sync-copy';

/** Don't flash the banner for refreshes that finish almost instantly. */
export const SHOW_DELAY_MS = 400;
/** How long the "up to date" confirmation stays on screen. */
export const SUCCESS_VISIBLE_MS = 4000;

type Shown = 'none' | 'syncing' | 'success';

/**
 * Transient banner that makes background sync visible: while the data layer is
 * pulling from or pushing to Mon Ami it shows progress (and, when local edits
 * are waiting to upload, asks the user to keep the device open), then confirms
 * when everything is up to date. Rendered in an overlay so the page never
 * shifts. Failures are not shown here: they persist, so they go to the global
 * banner (useUnsyncedWorkBanner, useSessionExpiredBanner).
 *
 * The banner has no controls: sync is automatic. The manual refresh lives in
 * the sidebar, and Settings > Sync has a troubleshooting one that reports
 * errors in detail.
 */
export default function SyncStatusBanner() {
  const { status, finishedAt, leftBehind, leftBehindContacts, leftBehindNotes, visible } = useSyncActivity();
  const { organizationId } = useCurrentPerson();
  const [shown, setShown] = useState<Shown>('none');
  const showedSyncing = useRef(false);

  // Sampled once when a visible sync starts (before the 400ms show delay, so
  // the copy is settled by the time the banner appears) rather than observed
  // live: this component is mounted on every route, so it must not re-run as
  // the push drains the pending documents. Only a yes/no is needed, so the
  // query stops at the first pending document. Deciding the copy from what was
  // pending at the start also keeps it from flipping mid-sync.
  const [uploading, setUploading] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    if (status !== 'syncing' || !visible || !organizationId) {
      setUploading(undefined);
      return;
    }
    let cancelled = false;
    Promise.all([hasPendingDocuments(organizationId), hasPendingCaseNotes(organizationId)])
      .then(([documents, notes]) => { if (!cancelled) setUploading(documents || notes); })
      .catch(() => { if (!cancelled) setUploading(false); });
    return () => { cancelled = true; };
  }, [status, visible, organizationId]);

  useEffect(() => {
    if (status === 'syncing') {
      // Background syncs (dashboard load, startup, unlock, tab mounts) run
      // silently; only a reconnect or a manual request shows progress.
      if (!visible) return;
      const timer = setTimeout(() => {
        showedSyncing.current = true;
        setShown('syncing');
      }, SHOW_DELAY_MS);
      return () => clearTimeout(timer);
    }

    if (status === 'success') {
      // Only confirm completion when the user actually saw the syncing state;
      // otherwise a sub-second background refresh would pop a banner for nothing.
      // Either way a success clears any earlier error.
      if (!showedSyncing.current) {
        setShown('none');
        return;
      }
      showedSyncing.current = false;
      setShown('success');
      const timer = setTimeout(() => setShown('none'), SUCCESS_VISIBLE_MS);
      return () => clearTimeout(timer);
    }

    // Idle or failed (the global banner reports failures): clear the flag so a
    // later brief sync cannot pop a stray confirmation.
    showedSyncing.current = false;
    setShown('none');
  }, [status, finishedAt, visible]);

  if (shown === 'none') return null;

  if (shown === 'syncing') {
    // Neutral copy until the check has resolved, so the text never swaps.
    return (
      <div
        role="status"
        data-sync-status="syncing"
        className="flex items-center justify-center gap-3 bg-deep-sea-green px-4 py-2 text-sm text-white shadow"
      >
        <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span>
          <span className="font-semibold">{uploading === true ? 'Uploading your work to Mon Ami…' : 'Syncing with Mon Ami…'}</span>
          {uploading === true && <> Please keep this device open until it finishes.</>}
        </span>
      </div>
    );
  }

  if (shown === 'success') {
    const leftBehindCopy = describeLeftBehind(leftBehind, leftBehindContacts, leftBehindNotes);
    return (
      <div
        role="status"
        data-sync-status="success"
        className="flex items-center justify-center gap-3 bg-green-50 border-b border-green-200 px-4 py-2 text-sm text-green-800"
      >
        <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
        {leftBehindCopy ? (
          <span>
            Sync finished, but {leftBehindCopy} couldn&apos;t be uploaded.{' '}
            <Link to="/" className="font-semibold underline">
              View on the dashboard
            </Link>
          </span>
        ) : (
          <span>Everything is up to date.</span>
        )}
      </div>
    );
  }

  return null;
}
